import React, { Fragment, useEffect, useState, useRef } from 'react'
import { useRouter } from 'next/router'

// third party components
import { Dialog, Transition } from '@headlessui/react'

// next components
import { namePublicFile, useViewport } from 'utils'
import { containsEmoji, containsSpecialChars } from 'utils/helpers'

// third party components
import { useWallet } from '@solana/wallet-adapter-react'
import { useDispatch } from 'react-redux'

// custom components
import Input from 'components/Input'
import Textarea from 'components/Input/Textarea'
import GradientButton from 'components/Button/GradientButton'
import Alert from 'components/Alert'

// images and icons
import CloseIcon from 'assets/icons/close-white-black-15x15.svg?component'
import EditorGreenIcon from 'assets/icons/editor-white.svg?component'
import TwitterWhiteIcon from 'assets/icons/twitter-white-20x20.svg?component'
import InstagramWhiteIcon from 'assets/icons/instagram-white-20x20.svg?component'
import WebsiteIcon from 'assets/icons/website-icon-20x20.svg?component'
import DripIcon from 'assets/icons/drip-icon-20x20.svg?component'

// styles
import styles from './index.module.scss'

// apis
import userAPI from 'apis/user'
import ImageWithFoster from 'components/ImageWithFoster'
import uploadFileToS3API from 'apis/aws'
import LoadingButton from 'components/Button/LoadingButton'
const EditProfileModal = props => {
  const { open, close, onSave } = props
  const router = useRouter()
  const { publicKey } = useWallet()
  const dispatch = useDispatch()
  const viewport = useViewport()
  const inputAvatarFile = useRef<any>(null)

  const [userName, setUsername] = useState('')
  const [bio, setBio] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [profilePictureUrl, setProfilePictureUrl] = useState('')
  const [igUrl, setIgUrl] = useState('')
  const [discordUrl, setDiscordUrl] = useState('')
  const [twitterUrl, setTwitterUrl] = useState('')
  const [websiteUrl, setWebSiteUrl] = useState('')
  const [telegramUrl, setTelegramUrl] = useState('')
  const [orgUserInfo, setOrgUserInfo] = useState<any>(null)
  const [enableSave, setEnableSave] = useState(false)
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [errorIGUrl, setErrorIGUrl] = useState('')
  const [errorTwitterUrl, setErrorTwitterUrl] = useState('')
  const [errorWebsiteUrl, setErrorWebsiteUrl] = useState('')
  const [errorLandUrl, setErrprLandUrl] = useState('')
  const [landUrl, setLandUrl] = useState('')
  const [dripUrl, setDripUrl] = useState('')
  const [errorDripUrl, setErrorDripUrl] = useState('')
  const [avatarPictureUrl, setAvatarPictureUrl] = useState('')
  const [avatarUploadingToS3, setAvatarUploadingToS3] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [editingAvatar, setEditingAvatar] = useState(null)

  const closeModal = () => {
    close()
  }

  const checkEnableSave = () => {
    if (orgUserInfo === null) {
      return
    }

    if (editingAvatar) {
      setEnableSave(true)
      return
    }

    if (userName && userName !== orgUserInfo.username) {
      setEnableSave(true)
      return
    }
    if (bio !== orgUserInfo.bio) {
      setEnableSave(true)
      return
    }
    if (userEmail && userEmail !== orgUserInfo.email) {
      setEnableSave(true)
      return
    }
    if (igUrl !== orgUserInfo.ig_url) {
      setEnableSave(true)
      return
    }
    if (twitterUrl !== orgUserInfo.twitter_url) {
      setEnableSave(true)
      return
    }
    if (dripUrl !== orgUserInfo.drip_url) {
      setEnableSave(true)
      return
    }
    if (landUrl !== orgUserInfo.three_land_url) {
      setEnableSave(true)
      return
    }
    if (websiteUrl !== orgUserInfo.site_url) {
      setEnableSave(true)
      return
    }
    if (profilePictureUrl && profilePictureUrl !== orgUserInfo.profile_picture && profilePictureUrl !== '') {
      setEnableSave(true)
      return
    }
    setEnableSave(false)
    return
  }

  const handleSaveUserInfo = async () => {
    setIsSaving(true)

    let validate_check = await userAPI.validateUsername({ username: userName })
    if (!validate_check) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      })
      setErrorMessage('This display name is already taken.')
      setIsSaving(false)
      return
    }
    setUsername(userName.trimEnd())
    setBio(bio?.replaceAll('\n', ' '))

    let profile_url = profilePictureUrl
    if (editingAvatar) {
      let response = await fetch(editingAvatar)
      let blob = await response.blob()
      const file = new File([blob], 'avatar.jpg', {
        type: 'image/jpeg',
      })
      const filename = namePublicFile('avatar.jpg')
      const { url } = await uploadFileToS3API(file, filename)
      profile_url = url
    }
    userAPI
      .update({
        wallet_id: publicKey?.toBase58() || '',
        username: userName.trimEnd(),
        bio: bio?.replaceAll('\n', ' '),
        email: userEmail,
        ig_url: igUrl,
        twitter_url: twitterUrl,
        drip_url: dripUrl,
        discord_url: discordUrl,
        telegram_url: telegramUrl,
        three_land_url: landUrl,
        site_url: websiteUrl,
        profile_picture: profile_url,
      })
      .then(
        response => {
          setIsSaving(false)
          if (response !== null) {
            dispatch({ type: 'set', profileInfo: response })
            if (userName && userName !== orgUserInfo.username) {
              router.push(`/${userName.trimEnd()}`, undefined, { shallow: true })
            }
            setOrgUserInfo(response)
            setEnableSave(false)
            setShowAlert(true)
            setTimeout(() => {
              onSave(response)
            }, 3000)
          }
        },
        error => {
          setIsSaving(false)
          console.log(error)
        }
      )
  }

  const [data, setData] = useState<any[]>([])

  useEffect(() => {
    setData([
      {
        id: 0,
        title: 'Updated successfully!',
        description: 'User profile is updated successfully!',
        type: 'success',
      },
    ])
  }, [])

  useEffect(() => {
    if (publicKey === null || !open) return
    userAPI.get(publicKey?.toBase58() || '').then(
      response => {
        if (response !== null) {
          const userInfo = response
          if (userInfo.profile_picture) {
            setProfilePictureUrl(userInfo.profile_picture)
          }
          setUsername(userInfo.username)
          setBio(userInfo.bio)
          setUserEmail(userInfo.email)
          setIgUrl(userInfo.ig_url)
          setTwitterUrl(userInfo.twitter_url)
          setDripUrl(userInfo.drip_url)
          setWebSiteUrl(userInfo.site_url)
          setDiscordUrl(userInfo.discord_url)
          setTelegramUrl(userInfo.telegram_url)
          setLandUrl(userInfo.three_land_url)
          setOrgUserInfo(userInfo)
        }
      },
      error => {
        console.log(error)
      }
    )
  }, [publicKey, open])

  useEffect(() => {
    checkEnableSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editingAvatar,
    userName,
    bio,
    userEmail,
    igUrl,
    discordUrl,
    twitterUrl,
    telegramUrl,
    dripUrl,
    landUrl,
    websiteUrl,
    profilePictureUrl,
  ])

  const updateAvatar = (event: any) => {
    const file = event.target.files
    if (file?.length === 0) return

    if (file[0].size > 10485760) {
      return
    }
    let avatr = URL.createObjectURL(file[0])

    setEditingAvatar(avatr)
  }

  return (
    <div>
      <Transition show={open} as={Fragment}>
        <Dialog as="div" className={styles.container} onClose={() => closeModal()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className={styles.bgView} />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex h-full lg:min-h-full items-center justify-center lg:p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className={styles.modalView}>
                  <div className={styles.close} onClick={() => closeModal()}>
                    <CloseIcon alt="" />
                  </div>
                  <div className={styles.editProfile}>Edit Profile</div>

                  <div className="w-full h-[calc(100vh-220px)] sm:h-[calc(100vh-470px)] mt-[30px] overflow-auto pr-[16px] sm:pr-[45px]">
                    {editingAvatar || profilePictureUrl !== '' ? (
                      <div className="relative w-[200px] h-[200px] mb-[30px] rounded-[10px] overflow-hidden">
                        <ImageWithFoster src={editingAvatar ?? profilePictureUrl} width={200} height={200} />
                        <div
                          className={styles.editorIcon}
                          onClick={() => {
                            inputAvatarFile.current.click()
                            setEditingAvatar(null)
                            setAvatarPictureUrl('')
                            setProfilePictureUrl(orgUserInfo.profile_picture)
                          }}
                        >
                          <EditorGreenIcon />
                        </div>
                        <input
                          type="file"
                          id="avatar-file"
                          accept="image/png, image/jpeg, image/gif"
                          ref={inputAvatarFile}
                          style={{ display: 'none' }}
                          disabled={avatarUploadingToS3}
                          onChange={event => {
                            updateAvatar(event)
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-[200px] h-[200px] relative cursor-pointer z-20 mb-[30px]">
                        <div className={styles.profileView} onClick={() => inputAvatarFile.current.click()}>
                          <div className="w-full h-full flex justify-center items-center">
                            <div>
                              <p className={styles.uploadText}>Upload File</p>
                              <p className={styles.supportText}>
                                Supported File Formats
                                <br />
                                .png and .jpeg
                              </p>
                              <p className={styles.greenText}>Max File Size 10mb</p>
                            </div>
                          </div>
                        </div>
                        <input
                          type="file"
                          id="avatar-file"
                          accept="image/png, image/jpeg, image/gif"
                          ref={inputAvatarFile}
                          style={{ display: 'none' }}
                          disabled={avatarUploadingToS3}
                          onChange={event => {
                            updateAvatar(event)
                          }}
                        />
                      </div>
                    )}

                    <div className={styles.subTitle}>Profile Information</div>

                    <div className="mt-[10px] lg:mt-[15px]">
                      <Input
                        error={errorMessage}
                        value={userName}
                        fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                        color="var(--text-gray-200-color)"
                        backgroundColor="var(--bg-101010-F3F2F0)"
                        borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                        borderRadius="23px"
                        placeholder="Username"
                        onChange={value => {
                          if (errorMessage.length > 0) setErrorMessage('')
                          setUsername(value)
                          if (containsSpecialChars(value) || containsEmoji(value))
                            setErrorMessage("Usernames can't contain the following special characters:  /:*?<>#")
                        }}
                      />
                    </div>
                    <div className="mt-[10px] lg:mt-[15px] h-fit">
                      <Textarea
                        value={bio}
                        fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                        color="var(--text-gray-200-color)"
                        backgroundColor="var(--bg-101010-F3F2F0)"
                        borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                        borderRadius="16px"
                        placeholder="Tell us a little about yourself..."
                        width="100%"
                        height="142px"
                        label01="Max Characters 1000"
                        label02={1000 - bio?.length === 1000 ? '' : `${1000 - bio?.length} Characters left`}
                        onChange={value => setBio(value)}
                      />
                    </div>
                    {/* <div className="mt-[10px] lg:mt-[15px]">
                      <Input
                        value={userEmail}
                        fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                        color="var(--text-gray-200-color)"
                        placeholder="Email Address"
                        borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                        borderRadius="10px"
                        onChange={value => setUserEmail(value)}
                      />
                    </div> */}
                    <div className="mt-[40px] lg:mt-[30px]">
                      <div className={styles.subTitle}>Socials</div>
                    </div>

                    <div className="mt-[10px] lg:mt-[15px] flex ">
                      <div className={styles.socialIcon}>
                        <TwitterWhiteIcon />
                      </div>
                      <div className="w-full ml-[10px] lg:ml-[25px]">
                        <Input
                          value={twitterUrl}
                          label01={errorTwitterUrl}
                          placeholder="Add your X(Twitter) profile link"
                          fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                          color="var(--text-gray-200-color)"
                          borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                          backgroundColor="var(--bg-101010-F3F2F0)"
                          borderRadius="23px"
                          onChange={value => {
                            if (value.includes('twitter.com/') || value.includes('x.com/')) {
                              setErrorTwitterUrl('')
                            } else {
                              setErrorTwitterUrl('Please enter a valid website url.')
                            }
                            setTwitterUrl(value)
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-[15px] flex">
                      <div className={styles.socialIcon}>
                        <InstagramWhiteIcon />
                      </div>

                      <div className="w-full ml-[10px] lg:ml-[25px]">
                        <Input
                          value={igUrl}
                          label01={errorIGUrl}
                          placeholder="Add your Instagram profile link"
                          fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                          color="var(--text-gray-200-color)"
                          borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                          backgroundColor="var(--bg-101010-F3F2F0)"
                          borderRadius="23px"
                          onChange={value => {
                            if (value.includes('instagram.com/')) {
                              setErrorIGUrl('')
                            } else {
                              setErrorIGUrl('Please enter a valid website url.')
                            }
                            setIgUrl(value)
                          }}
                        />
                      </div>
                    </div>

                    <div className="mt-[10px] lg:mt-[15px] flex ">
                      <div className={styles.socialIcon}>
                        <WebsiteIcon />
                      </div>
                      <div className="w-full ml-[10px] lg:ml-[25px]">
                        <Input
                          value={websiteUrl}
                          label01={errorWebsiteUrl}
                          placeholder="Add your personal website link"
                          fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                          color="var(--text-gray-200-color)"
                          borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                          backgroundColor="var(--bg-101010-F3F2F0)"
                          borderRadius="23px"
                          onChange={value => {
                            if (value.includes('http')) {
                              setErrorWebsiteUrl('')
                            } else {
                              setErrorWebsiteUrl('Please enter a valid website url.')
                            }
                            setWebSiteUrl(value)
                          }}
                        />
                      </div>
                    </div>

                    <div className="mt-[10px] lg:mt-[15px] flex ">
                      <div className={styles.socialIcon}>
                        <DripIcon />
                      </div>
                      <div className="w-full ml-[10px] lg:ml-[25px]">
                        <Input
                          value={dripUrl}
                          label01={errorDripUrl}
                          placeholder="Add your DRiP profile link"
                          fontSize={viewport === 'xs' || viewport === 'sm' || viewport === 'md' ? '14px' : '16px'}
                          color="var(--text-gray-200-color)"
                          borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                          backgroundColor="var(--bg-101010-F3F2F0)"
                          borderRadius="23px"
                          onChange={value => {
                            if (value.includes('http')) {
                              setErrorDripUrl('')
                            } else {
                              setErrorDripUrl('Please enter a valid website url.')
                            }
                            setDripUrl(value)
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-[30px] lg:mt-[30px] w-full flex justify-center lg:justify-end items-center gap-6 pr-[16px] sm:pr-[45px]">
                    {isSaving ? (
                      <LoadingButton fullWidth />
                    ) : (
                      <GradientButton
                        fullWidth
                        label="Save"
                        disabled={!enableSave}
                        onClick={() => {
                          handleSaveUserInfo()
                        }}
                      />
                    )}
                  </div>
                  <Alert
                    open={showAlert}
                    close={() => {
                      setShowAlert(false)
                    }}
                    data={data}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default EditProfileModal
